import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { Shield, Save } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface PrivacySettings {
  id: string;
  company_name: string;
  responsible_name: string;
  vat_number: string;
  legal_address: string;
  postal_code: string;
  city: string;
  phone: string;
  contact_email: string;
  privacy_policy: string;
  created_at: string;
  updated_at: string;
}

export default function PrivacyPage() {
  const { profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PrivacySettings | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [responsibleName, setResponsibleName] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [legalAddress, setLegalAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [privacyPolicy, setPrivacyPolicy] = useState('');

  useEffect(() => {
    if (!authLoading && profile?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [profile, authLoading, navigate]);

  useEffect(() => {
    if (profile?.role === 'admin') {
      fetchPrivacySettings();
    }
  }, [profile]);

  const fetchPrivacySettings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('privacy_settings')
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error fetching privacy settings:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile caricare le impostazioni privacy',
        variant: 'destructive',
      });
    } else if (data) {
      setSettings(data);
      setCompanyName(data.company_name || '');
      setResponsibleName(data.responsible_name || '');
      setVatNumber(data.vat_number || '');
      setLegalAddress(data.legal_address || '');
      setPostalCode(data.postal_code || '');
      setCity(data.city || '');
      setPhone(data.phone || '');
      setContactEmail(data.contact_email || '');
      setPrivacyPolicy(data.privacy_policy || '');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (settings) {
        const { error } = await supabase
          .from('privacy_settings')
          .update({
            company_name: companyName,
            responsible_name: responsibleName,
            vat_number: vatNumber,
            legal_address: legalAddress,
            postal_code: postalCode,
            city: city,
            phone: phone,
            contact_email: contactEmail,
            privacy_policy: privacyPolicy,
            updated_at: new Date().toISOString(),
          })
          .eq('id', settings.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('privacy_settings')
          .insert({
            company_name: companyName,
            responsible_name: responsibleName,
            vat_number: vatNumber,
            legal_address: legalAddress,
            postal_code: postalCode,
            city: city,
            phone: phone,
            contact_email: contactEmail,
            privacy_policy: privacyPolicy,
          });

        if (error) throw error;
      }

      toast({
        title: 'Successo',
        description: 'Impostazioni privacy salvate con successo',
      });

      fetchPrivacySettings();
    } catch (error: any) {
      console.error('Error saving privacy settings:', error);
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile salvare le impostazioni',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || profile?.role !== 'admin') {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Privacy e GDPR
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Gestisci le informazioni legali e la privacy policy
            </p>
          </div>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Dati Aziendali</CardTitle>
              <CardDescription>
                Inserisci i dati del titolare del trattamento dati
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <>
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor="company-name">Nome Società / Ragione Sociale</Label>
                    <Input
                      id="company-name"
                      placeholder="Es. Acme S.r.l."
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="responsible-name">Nome Responsabile</Label>
                    <Input
                      id="responsible-name"
                      placeholder="Es. Mario Rossi"
                      value={responsibleName}
                      onChange={(e) => setResponsibleName(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vat-number">Partita IVA</Label>
                    <Input
                      id="vat-number"
                      placeholder="Es. 12345678901"
                      value={vatNumber}
                      onChange={(e) => setVatNumber(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="legal-address">Indirizzo Sede Legale</Label>
                      <Input
                        id="legal-address"
                        placeholder="Es. Via Roma, 123"
                        value={legalAddress}
                        onChange={(e) => setLegalAddress(e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal-code">CAP</Label>
                      <Input
                        id="postal-code"
                        placeholder="Es. 00100"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="city">Città</Label>
                    <Input
                      id="city"
                      placeholder="Es. Roma"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Telefono</Label>
                      <Input
                        id="phone"
                        placeholder="Es. +39 06 1234567"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email">Email per Richieste e Reclami</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        placeholder="Es. privacy@esempio.com"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Privacy Policy</CardTitle>
              <CardDescription>
                Inserisci il testo completo della privacy policy che gli utenti dovranno accettare durante la registrazione
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-96 w-full" />
              ) : (
                <Textarea
                  placeholder="Inserisci qui il testo completo della privacy policy..."
                  value={privacyPolicy}
                  onChange={(e) => setPrivacyPolicy(e.target.value)}
                  rows={20}
                  className="font-mono text-sm"
                />
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={fetchPrivacySettings}
              disabled={loading || saving}
            >
              Annulla modifiche
            </Button>
            <Button onClick={handleSave} disabled={loading || saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvataggio...' : 'Salva impostazioni'}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
